/**
 * RICHILL X attribution -> GA4 bridge.
 *
 * The global `utm-capture` snippet owns session persistence in richill_utm_v1.
 * This isolated helper only emits X landing and LINE friend-add click events;
 * it never rewrites links, UTM parameters, or the existing GA4 config.
 */
(function (root, factory) {
  var api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root && root.document) {
    api.init(root);
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var STORAGE_KEY = 'richill_utm_v1';
  var LANDING_EVENT_KEY_PREFIX = 'richill_x_landing_sent_v1:';
  var CAMPAIGN_PATTERN = /^richill_x_\d{6}_.+$/i;
  var CONTENT_PATTERN = /^.+_.+$/;

  function normalizeXAttribution(value, landingPath) {
    if (!value || String(value.utm_source || '').toLowerCase() !== 'x' || String(value.utm_medium || '').toLowerCase() !== 'social') {
      return null;
    }

    var campaign = String(value.utm_campaign || '');
    var content = String(value.utm_content || '');

    return {
      utm_source: 'x',
      utm_medium: 'social',
      utm_campaign: campaign,
      utm_content: content,
      campaign_name_valid: CAMPAIGN_PATTERN.test(campaign),
      content_name_valid: CONTENT_PATTERN.test(content),
      landing_path: landingPath || ''
    };
  }

  function parseXAttribution(href) {
    var url;

    try {
      url = new URL(href);
    } catch (error) {
      return null;
    }

    return normalizeXAttribution({
      utm_source: url.searchParams.get('utm_source'),
      utm_medium: url.searchParams.get('utm_medium'),
      utm_campaign: url.searchParams.get('utm_campaign'),
      utm_content: url.searchParams.get('utm_content')
    }, url.pathname);
  }

  function readXAttribution(storage, root) {
    var value = root && root.__richillUtm;

    try {
      if (!value || Object.keys(value).length === 0) {
        var stored = storage.getItem(STORAGE_KEY);
        value = stored ? JSON.parse(stored) : null;
      }
    } catch (error) {
      value = value || null;
    }

    return normalizeXAttribution(value);
  }

  function memoryStorage() {
    var values = {};
    return {
      getItem: function (key) { return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : null; },
      setItem: function (key, value) { values[key] = String(value); }
    };
  }

  function sessionStorageOrMemory(root) {
    try {
      return root.sessionStorage || memoryStorage();
    } catch (error) {
      return memoryStorage();
    }
  }

  function isLineFriendAddAnchor(anchor, baseHref) {
    if (!anchor || typeof anchor.getAttribute !== 'function') return false;
    if (anchor.hasAttribute('data-line-friend-add')) return true;

    var rawHref = anchor.getAttribute('href');
    if (!rawHref) return false;

    var url;
    try {
      url = new URL(rawHref, baseHref);
    } catch (error) {
      return false;
    }

    var host = url.hostname.toLowerCase();
    var path = url.pathname.toLowerCase();
    if (host === 'social-plugins.line.me') return false;

    var isLineHost = host === 'lin.ee' || host === 'line.me' || host === 'www.line.me' || host === 'liff.line.me';
    if (!isLineHost) return false;

    if (host === 'lin.ee') return true;
    if (/\/(r\/)?ti\/p\//.test(path) || /\/r\/oamessage\//.test(path)) return true;

    var label = [
      anchor.textContent || '',
      anchor.getAttribute('aria-label') || '',
      anchor.getAttribute('title') || '',
      anchor.getAttribute('alt') || ''
    ].join(' ');

    return /(友だち追加|友達追加|line\s*(登録|公式|追加))/i.test(label);
  }

  function eventParameters(attribution, anchor, locationHref) {
    var href = anchor && anchor.href ? anchor.href : '';
    var destinationHost = '';

    try {
      destinationHost = new URL(href, locationHref).hostname;
    } catch (error) {
      destinationHost = '';
    }

    return {
      link_url: href,
      line_destination_host: destinationHost,
      link_id: anchor && anchor.id ? anchor.id : '',
      link_text: anchor && anchor.textContent ? anchor.textContent.trim().slice(0, 100) : '',
      x_utm_source: attribution ? attribution.utm_source : '(not_x)',
      x_utm_medium: attribution ? attribution.utm_medium : '(not_x)',
      x_utm_campaign: attribution ? attribution.utm_campaign : '(not_x)',
      x_utm_content: attribution ? attribution.utm_content : '(not_x)',
      x_campaign_name_valid: attribution ? attribution.campaign_name_valid : false,
      x_content_name_valid: attribution ? attribution.content_name_valid : false,
      transport_type: 'beacon'
    };
  }

  function sendEvent(root, name, params) {
    if (typeof root.gtag === 'function') {
      root.gtag('event', name, params);
      return true;
    }
    return false;
  }

  function init(root) {
    var storage = sessionStorageOrMemory(root);
    var landingAttribution = parseXAttribution(root.location.href);
    var attribution = landingAttribution || readXAttribution(storage, root);

    if (landingAttribution) {
      var landingEventKey = LANDING_EVENT_KEY_PREFIX + landingAttribution.utm_campaign + ':' + landingAttribution.utm_content;
      try {
        if (!storage.getItem(landingEventKey)) {
          sendEvent(root, 'richill_x_landing', {
            x_utm_source: landingAttribution.utm_source,
            x_utm_medium: landingAttribution.utm_medium,
            x_utm_campaign: landingAttribution.utm_campaign,
            x_utm_content: landingAttribution.utm_content,
            x_campaign_name_valid: landingAttribution.campaign_name_valid,
            x_content_name_valid: landingAttribution.content_name_valid,
            landing_path: landingAttribution.landing_path,
            transport_type: 'beacon'
          });
          storage.setItem(landingEventKey, '1');
        }
      } catch (error) {
        // Storage restrictions must never block page rendering or GA4.
      }
    }

    root.document.addEventListener('click', function (event) {
      var target = event.target;
      var anchor = target && typeof target.closest === 'function' ? target.closest('a') : null;
      if (!isLineFriendAddAnchor(anchor, root.location.href)) return;

      attribution = readXAttribution(storage, root) || attribution;
      sendEvent(root, 'line_friend_add_click', eventParameters(attribution, anchor, root.location.href));
    }, true);
  }

  return {
    STORAGE_KEY: STORAGE_KEY,
    parseXAttribution: parseXAttribution,
    readXAttribution: readXAttribution,
    isLineFriendAddAnchor: isLineFriendAddAnchor,
    eventParameters: eventParameters,
    sessionStorageOrMemory: sessionStorageOrMemory,
    init: init
  };
});
